"use client"

import * as React from "react"
import {
  Activity,
  BookOpen,
  Box,
  Building2,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  PlugZap,
  Shield,
  Truck,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react"

import { useProfile } from "../hooks/UserHooks"
import { NavMain } from "./nav-main"
import { NavProjects } from "./nav-projects"
import { NavSecondary } from "./nav-secondary"
import { NavUser } from "./nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import logoSm from "@/assets/images/logo-sm.png"
import logoDark from "@/assets/images/logo-dark.png"

type IconType = LucideIcon

type NavMainItem = {
  title: string
  url: string
  icon?: IconType
  isActive?: boolean
  items?: {
    title: string
    url: string
  }[]
}

type ProjectItem = {
  name: string
  url: string
  icon: IconType
}

type SecondaryItem = {
  title: string
  url: string
  icon: IconType
  external?: boolean
}

function buildNavMainItems(userProfile: any): NavMainItem[] {
  const role = userProfile?.role ?? ""

  const hasRole = (roles: string[] = []) =>
    roles.length === 0 || roles.includes(role)

  const sections: NavMainItem[] = []

  // Dashboard
  if (hasRole()) {
    sections.push({
      title: "Dashboard",
      url: "/",
      icon: LayoutDashboard,
      isActive: true,
      items: [
        {
          title: "Visão geral",
          url: "/",
        },
      ],
    })
  }

  // Empresas
  const companyItems = [
    {
      title: "Ad Networks",
      url: "/ad-network",
      roles: ["ROLE_SUPER_ADMIN"],
    },
    {
      title: "Anunciantes",
      url: "/advertiser",
      roles: ["ROLE_SUPER_ADMIN"],
    },
    {
      title: "Agências",
      url: "/agency",
      roles: ["ROLE_SUPER_ADMIN"],
    },
    {
      title: "Publishers",
      url: "/publisher",
      roles: ["ROLE_SUPER_ADMIN", "ROLE_AGENCY_ADMIN", "ROLE_AD_NETWORK_ADMIN"],
    },
  ].filter((item) => hasRole(item.roles))

  if (companyItems.length) {
    sections.push({
      title: "Empresas",
      url: "#",
      icon: Building2,
      items: companyItems.map(({ title, url }) => ({ title, url })),
    })
  }

  // Entrega
  const deliveryItems = [
    {
      title: "Campanhas",
      url: "/campaign",
      roles: [],
    },
    {
      title: "Pedidos",
      url: "/order",
      roles: [],
    },
    {
      title: "Criativos",
      url: "/creative",
      roles: [],
    },
  ].filter((item) => hasRole(item.roles))

  if (deliveryItems.length) {
    sections.push({
      title: "Entrega",
      url: "#",
      icon: Truck,
      items: deliveryItems.map(({ title, url }) => ({ title, url })),
    })
  }

  // Inventário
  const inventoryItems = [
    {
      title: "Sites",
      url: "/site",
      roles: [],
    },
    {
      title: "Blocos de anúncios",
      url: "/placement",
      roles: ["ROLE_SUPER_ADMIN"],
    },
    {
      title: "TAGs",
      url: "/tag",
      roles: [],
    },
  ].filter((item) => hasRole(item.roles))

  if (inventoryItems.length) {
    sections.push({
      title: "Inventário",
      url: "#",
      icon: Box,
      items: inventoryItems.map(({ title, url }) => ({ title, url })),
    })
  }

  // Admin
  const adminItems = [
    {
      title: "Usuários",
      url: "/user",
      roles: [],
    },
    {
      title: "Histórico de alterações",
      url: "/audit-log",
      roles: ["ROLE_SUPER_ADMIN"],
    },
  ].filter((item) => hasRole(item.roles))

  if (adminItems.length) {
    sections.push({
      title: "Admin",
      url: "#",
      icon: UserCog,
      items: adminItems.map(({ title, url }) => ({ title, url })),
    })
  }

  // Sair da personificação
  if (typeof userProfile?.impersonatedUser === "object") {
    sections.push({
      title: "Sair da personificação",
      url: "/exit-impersonation",
      icon: Users,
      items: [
        {
          title: "Sair da personificação",
          url: "/exit-impersonation",
        },
      ],
    })
  }

  return sections
}

function buildProjects(userProfile: any): ProjectItem[] {
  const role = userProfile?.role ?? ""

  const hasRole = (roles: string[] = []) =>
    roles.length === 0 || roles.includes(role)

  const items: ProjectItem[] = []

  // Monitoramento
  if (hasRole()) {
    items.push({
      name: "Monitoramento",
      url: "/site-monitor",
      icon: Activity,
    })
  }

  // Brand Safety
  if (hasRole()) {
    items.push({
      name: "Brand Safety",
      url: "/blocked-keywords",
      icon: Shield,
    })
  }

  // Integrações
  if (hasRole()) {
    items.push({
      name: "Integrações",
      url: "/integrations",
      icon: PlugZap,
    })
  }

  // API Keys
  if (
    hasRole(["ROLE_SUPER_ADMIN", "ROLE_AGENCY_ADMIN", "ROLE_AD_NETWORK_ADMIN"])
  ) {
    items.push({
      name: "API Keys",
      url: "/api-keys",
      icon: KeyRound,
    })
  }

  return items
}

function buildSecondaryItems(userProfile: any): SecondaryItem[] {
  const role = userProfile?.role ?? ""

  const hasRole = (roles: string[] = []) =>
    roles.length === 0 || roles.includes(role)

  const items: SecondaryItem[] = []

  // Wiki (externo)
  if (hasRole()) {
    items.push({
      title: "Wiki",
      url: "https://docs.adpulse.com.br",
      icon: BookOpen,
      external: true,
    })
  }

  // Suporte
  if (
    hasRole(["ROLE_SUPER_ADMIN", "ROLE_AGENCY_ADMIN", "ROLE_AD_NETWORK_ADMIN"])
  ) {
    items.push({
      title: "Suporte",
      url: "/support",
      icon: LifeBuoy,
    })
  }

  return items
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { userProfile } = useProfile()
  const { state } = useSidebar()

  const navMain = React.useMemo(
    () => buildNavMainItems(userProfile),
    [userProfile]
  )

  const projects = React.useMemo(
    () => buildProjects(userProfile),
    [userProfile]
  )
  const secondary = React.useMemo(
    () => buildSecondaryItems(userProfile),
    [userProfile]
  )

  const user = {
    name: userProfile?.name ?? "Usuário",
    email: userProfile?.email ?? "",
    avatar: "/avatars/shadcn.jpg",
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <div className="flex items-center justify-center px-2 py-1">
          <img
            src={state === "collapsed" ? logoSm : logoDark}
            alt="adPulse"
            className={state === "collapsed" ? "h-8 w-auto" : "h-6 w-auto"}
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
        <NavProjects projects={projects} />
      </SidebarContent>
      <SidebarFooter>
        <NavSecondary items={secondary} />
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}
